import {Component, forwardRef, Input, OnInit, SimpleChanges} from '@angular/core';
import {  NG_VALUE_ACCESSOR, Validators, FormGroup, FormControl, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {Subscription} from 'rxjs';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {distinctUntilChanged, tap} from 'rxjs/operators';
import {UntilDestroy} from '@ngneat/until-destroy';
import {formControlErrorKeys, formControlErrorMessage, setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import { ApplicationTypes, compareMatch, LoanPurposeOption, LoanPurposeValueOptions, OverdraftRenewalPurposeOptions, AllLoanPurposeOptions, LoanPurposeValue, TransactionValue, LoanPurpose, LoanPurposeIds } from '@portal-workspace/grow-shared-library';
import { MatOptionModule } from '@angular/material/core';

import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';

const isDealerOrPrivateOrSALB = (type: string | undefined) => ['Dealer Sale', 'Private Sale', 'Sales and Lease Back'].includes(type || '');
const isRefinanceOrBalloon = (type: string | undefined) => ['Refinance', 'Balloon Refinance'].includes(type || '');
const isCapitalRaise = (type: string | undefined) => type === 'Capital Raise';

@UntilDestroy()
@Component({
  selector: 'loan-purpose-selection',
  templateUrl: './loan-purpose-selection.component.html',
  styleUrls: ['./loan-purpose-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LoanPurposeSelectionComponent),
      multi: true,
    },
    {
      provide: MARK,
      useExisting: forwardRef(() => LoanPurposeSelectionComponent),
    },
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatSlideToggleModule,
    MatInputModule,
  ],
})
export class LoanPurposeSelectionComponent
  extends AbstractControlValueAccessor<LoanPurposeValue>
  implements OnInit, Mark
{
  @Input({required: false}) transactionType: TransactionValue | null = null;
  @Input({required: true}) applicationType: ApplicationTypes | null = null;
  @Input({ required: false }) hasActiveOverdraftAccount: boolean = false;
  @Input({ required: false }) limitIncreaseDisabled?: boolean;
  @Input({ required: false }) limitDecreaseDisabled?: boolean;
  @Input({ required: false }) renewalDisabled?: boolean;

  private _incomeGenSub?: Subscription;
  private _financeCommitmentSub?: Subscription;
  private _isLoadingFromApi = false;
  
  // Conditional field inputs
  showIncomeGeneratingAsset = false;
  showExpectedMonthlyIncome = false;
  showExistingFinanceCommitments = false;
  showLender = false;
  showLoanValue = false;
  showTaxBalance = false;
  showPurchasePrice = false;
  showPurposeDescription = false;
  furtherDescriptionLabel = '';

  formGroup: FormGroup<{
    selection: FormControl<LoanPurposeOption | null>;
    isIncomeGeneratingAsset: FormControl<boolean | null>;
    expectedMonthlyIncome: FormControl<number | null>;
    hasExistingFinanceCommitments: FormControl<boolean | null>;
    lender: FormControl<string | null>;
    loanValue: FormControl<number | null>;
    taxBalance: FormControl<number | null>;
    purchasePrice: FormControl<number | null>;
    purposeDescription: FormControl<string | null>;
  }>;
  formControl: FormControl<LoanPurposeOption | null>;
  subscription: Subscription;
  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  options = LoanPurposeValueOptions;
  filteredOptions = LoanPurposeValueOptions;

  readonly READ_ONLY_TYPES: LoanPurpose[] = [
    'Purchase of Financial Instruments',
    'Purchase of Residential Property',
  ];

  constructor(private formBuilder: FormBuilder) {
    super();
    this.formControl = formBuilder.control(null, [Validators.required]);
    this.formGroup = formBuilder.group({
      selection: this.formControl,
      isIncomeGeneratingAsset: formBuilder.control<boolean | null>(null),
      expectedMonthlyIncome: formBuilder.control<number | null>(null),
      hasExistingFinanceCommitments: formBuilder.control<boolean | null>(null),
      lender: formBuilder.control<string | null>(null),
      loanValue: formBuilder.control<number | null>(null),
      taxBalance: formBuilder.control<number | null>(null),
      purchasePrice: formBuilder.control<number | null>(null),
      purposeDescription: formBuilder.control<string | null>(null),
    });
    this.subscription = this.formControl.valueChanges
      .pipe(
        distinctUntilChanged(compareMatch),
        tap((r: LoanPurposeOption) => {
          this.propagateChange(r);
        })
      )
      .subscribe();
  }

  compareLoanPurpose = (a: LoanPurposeValue | null, b: LoanPurposeValue | null) => {
    return a && b && a.id === b.id;
  }

  override writeValue(obj: LoanPurposeValue | null): void {
    if (obj) {
      // Set flag to prevent subscription interference
      this._isLoadingFromApi = true;
      
      const fullOption = AllLoanPurposeOptions.find(opt => opt.id === obj.id);
      if (fullOption) {
        // Update filtered options
        this.updateFilteredOptions();
        
        // Ensure the option is in filtered list
        if (!this.filteredOptions.some(opt => opt.id === obj.id)) {
          console.log('Adding selected option to filtered list:', fullOption);
          this.filteredOptions = [...this.filteredOptions, fullOption];
        }

        // Set the value first
        this.formControl.setValue(fullOption, { emitEvent: false });
        
        // Reset visibility and handle prompts based on the selected option
        this.resetVisibility();
        
        // Handle conditional visibility based on API data FIRST
        this.handleApiDataConditionalVisibility(obj, fullOption);
        
        // Now set the form values from API data BEFORE setting up subscriptions
        this.formGroup.patchValue({
          isIncomeGeneratingAsset: obj.isIncomeGeneratingAsset ?? null,
          expectedMonthlyIncome: obj.expectedMonthlyIncome ?? null,
          hasExistingFinanceCommitments: obj.hasExistingFinanceCommitments ?? null,
          lender: obj.lender ?? null,
          loanValue: obj.loanValue ?? null,
          taxBalance: obj.taxBalance ?? null,
          purchasePrice: obj.purchasePrice ?? null,
          purposeDescription: obj.purposeDescription ?? null
        }, { emitEvent: false });

        // Now handle additional prompts AFTER API data is set
        this.handleAdditionalPrompts(fullOption, true);
        
        // Update validators after everything is set
        this.updateValidators(fullOption);
      } else {
        console.warn(`No matching loan purpose found for id: ${obj.id} in full options list`);
      }
      
      // Reset flag after API loading is complete
      this._isLoadingFromApi = false;
    } else {
      this.updateFilteredOptions();
      this.formControl.setValue(null, { emitEvent: false });
      this.formGroup.patchValue({
        isIncomeGeneratingAsset: false,
        expectedMonthlyIncome: null,
        hasExistingFinanceCommitments: false,
        lender: null,
        loanValue: null,
        taxBalance: null,
        purchasePrice: null,
        purposeDescription: null
      }, { emitEvent: false });
      this.resetVisibility();
    }
  }

  private handleApiDataConditionalVisibility(obj: NonNullable<LoanPurposeValue>, fullOption: LoanPurposeOption) {
    // Handle income generating asset visibility based on API data
    if (obj.isIncomeGeneratingAsset !== undefined) {
      this.showIncomeGeneratingAsset = true;
      if (obj.isIncomeGeneratingAsset) {
        this.showExpectedMonthlyIncome = true;
      }
    }

    // Handle existing finance commitments visibility based on API data
    if (obj.hasExistingFinanceCommitments !== undefined) {
      this.showExistingFinanceCommitments = true;
      if (obj.hasExistingFinanceCommitments) {
        this.showLender = true;
        // If lender has a value, also show loan value field
        if (obj.lender) {
          this.showLoanValue = true;
        }
      }
    }

    // Handle specific loan purpose conditional fields based on API data
    switch (fullOption.name) {
      case 'Purchase Additional Asset':
        this.showIncomeGeneratingAsset = true;
        // If API has income generating asset data, show expected monthly income
        if (obj.isIncomeGeneratingAsset) {
          this.showExpectedMonthlyIncome = true;
        }
        break;
      case 'Replace Existing Asset':
        this.showExistingFinanceCommitments = true;
        // If API has finance commitments data, show lender
        if (obj.hasExistingFinanceCommitments) {
          this.showLender = true;
          // If lender has a value, also show loan value field
          if (obj.lender) {
            this.showLoanValue = true;
          }
        }
        break;
      case 'Refinance Existing Borrowing':
        this.showLender = true;
        // If lender has a value, also show loan value field
        if (obj.lender) {
          this.showLoanValue = true;
        }
        break;
      case 'Refinance ATO Debt':
        this.showTaxBalance = true;
        break;
      case 'Purchase of Another Business':
        this.showPurchasePrice = true;
        break;
      case 'Business Expansion/Growth Capital':
        this.showPurposeDescription = true;
        this.furtherDescriptionLabel = 'please explain how funds will be used';
        break;
      case 'Managing Cashflow':
        this.showPurposeDescription = true;
        this.furtherDescriptionLabel = 'please explain why a facility is needed';
        break;
    }
  }

  private updateValidators(selectedOption: LoanPurposeOption) {
    // Reset all form controls first
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.get(key)?.clearValidators();
      this.formGroup.get(key)?.updateValueAndValidity();
    });

    // Add validators based on selected option
    if (selectedOption) {
      // Income generating asset validation
      if (this.showIncomeGeneratingAsset) {
        this.formGroup.get('isIncomeGeneratingAsset')?.setValidators([Validators.required]);
      }
      
      // Expected monthly income validation
      if (this.showExpectedMonthlyIncome) {
        this.formGroup.get('expectedMonthlyIncome')?.setValidators([Validators.required]);
      }

      // Finance commitments validation
      if (this.showExistingFinanceCommitments) {
        this.formGroup.get('hasExistingFinanceCommitments')?.setValidators([Validators.required]);
        
        // If has finance commitments is true, validate lender and loan value
        if (this.showLender) {
          this.formGroup.get('lender')?.setValidators([Validators.required]);
        }
        if (this.showLoanValue) {
          this.formGroup.get('loanValue')?.setValidators([Validators.required]);
        }
      }

      // Direct lender validation (for Refinance Existing Borrowing)
      if (this.showLender && !this.showExistingFinanceCommitments) {
        this.formGroup.get('lender')?.setValidators([Validators.required]);
      }

      // Direct loan value validation (for Refinance Existing Borrowing)
      if (this.showLoanValue && !this.showExistingFinanceCommitments) {
        this.formGroup.get('loanValue')?.setValidators([Validators.required]);
      }

      // Tax balance validation
      if (this.showTaxBalance) {
        this.formGroup.get('taxBalance')?.setValidators([Validators.required]);
      }

      // Purchase price validation  
      if (this.showPurchasePrice) {
        this.formGroup.get('purchasePrice')?.setValidators([Validators.required]);
      }

      // Purpose description validation
      if (this.showPurposeDescription) {
        this.formGroup.get('purposeDescription')?.setValidators([Validators.required]);
      }
    }

    // Update validity
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.get(key)?.updateValueAndValidity();
    });
  }

  private resetVisibility() {
    this.showIncomeGeneratingAsset = false;
    this.showExpectedMonthlyIncome = false;
    this.showExistingFinanceCommitments = false;
    this.showLender = false;
    this.showLoanValue = false;
    this.showTaxBalance = false;
    this.showPurchasePrice = false;
    this.showPurposeDescription = false;
    this.furtherDescriptionLabel = '';
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.updateFilteredOptions();

    this.formControl.valueChanges
      .pipe(
        tap((r: LoanPurposeOption | null) => {
          if (r && !this._isLoadingFromApi) {
            this.handleAdditionalPrompts(r);            
            this.propagateChange(this.doWriteValue(null));
          }
        })
      )
      .subscribe();

  this.formGroup.valueChanges
    .pipe(
      tap(() => {
        if (this.formControl.value && !this._isLoadingFromApi) {
          this.propagateChange(this.doWriteValue(null));
        }
      })
    )
    .subscribe();
  }

  // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
  ngOnChanges(changes: SimpleChanges): void {
    // Update filtered options when hasActiveOverdraftAccount changes
    if (changes['hasActiveOverdraftAccount'] || changes['applicationType'] || changes['transactionType']) {
      this.updateFilteredOptions();
    }
    if (changes['transactionType']) {
      // Reset all additional fields and errors
      this.resetField('isIncomeGeneratingAsset', null);
      this.resetField('expectedMonthlyIncome', null);
      this.resetField('hasExistingFinanceCommitments', null);
      this.resetField('lender', null);
      this.resetField('loanValue', null);
      this.resetField('taxBalance', null);
      this.resetField('purchasePrice', null);
      this.resetField('purposeDescription', null);
  
      // Reset all additional prompts/flags
      this.showIncomeGeneratingAsset = false;
      this.showExpectedMonthlyIncome = false;
      this.showExistingFinanceCommitments = false;
      this.showLender = false;
      this.showLoanValue = false;
      this.showTaxBalance = false;
      this.showPurchasePrice = false;
      this.showPurposeDescription = false;
      this.furtherDescriptionLabel = '';
  
      // Unsubscribe from any previous subscriptions
      if (this._incomeGenSub) this._incomeGenSub.unsubscribe();
      if (this._financeCommitmentSub) this._financeCommitmentSub.unsubscribe();
  
      // If a loan purpose is already selected, re-apply prompts
      if (this.formControl.value) {
        this.handleAdditionalPrompts(this.formControl.value);
      }
    }
    if (changes['transactionType'] || changes['applicationType']) {
      this.updateFilteredOptions();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  doWriteValue(_: LoanPurposeValue | null): LoanPurposeValue {
    return {
      id: LoanPurposeIds[this.formControl.value?.name as LoanPurpose] ?? 0,
      name: this.formControl.value?.name as LoanPurpose,
      isIncomeGeneratingAsset: this.formGroup.get('isIncomeGeneratingAsset')?.value ?? undefined,
      expectedMonthlyIncome: this.formGroup.get('expectedMonthlyIncome')?.value ?? undefined,
      hasExistingFinanceCommitments: this.formGroup.get('hasExistingFinanceCommitments')?.value ?? undefined,
      lender: this.formGroup.get('lender')?.value ?? undefined,
      loanValue: this.formGroup.get('loanValue')?.value ?? undefined,
      taxBalance: this.formGroup.get('taxBalance')?.value ?? undefined,
      purchasePrice: this.formGroup.get('purchasePrice')?.value ?? undefined,
      purposeDescription: this.formGroup.get('purposeDescription')?.value ?? undefined,
    };
  }

  mark() {
    this.formGroup.markAllAsTouched();
  }

  private resetField(field: keyof typeof this.formGroup.controls, value: any = null) {
    const control = this.formGroup.get(field);
    if (control) {
      control.clearValidators();
      control.setValue(value);
      control.markAsPristine();
      control.markAsUntouched();
      control.updateValueAndValidity();
    }
  }

  handleAdditionalPrompts(value: LoanPurposeOption, skipFieldReset = false) {
    // Reset all visibility flags
    this.showIncomeGeneratingAsset = false;
    this.showExpectedMonthlyIncome = false;
    this.showExistingFinanceCommitments = false;
    this.showLender = false;
    this.showLoanValue = false;
    this.showTaxBalance = false;
    this.showPurchasePrice = false;
    this.showPurposeDescription = false;
    this.furtherDescriptionLabel = '';
  
    // Only reset form fields if not skipping (i.e., for user interactions, not API data)
    if (!skipFieldReset) {
      this.resetField('isIncomeGeneratingAsset', null);
      this.resetField('expectedMonthlyIncome', null);
      this.resetField('hasExistingFinanceCommitments', null);
      this.resetField('lender', null);
      this.resetField('loanValue', null);
      this.resetField('taxBalance', null);
      this.resetField('purchasePrice', null);
      this.resetField('purposeDescription', null);
    }
  
    // Unsubscribe previous subscriptions
    if (this._incomeGenSub) this._incomeGenSub.unsubscribe();
    if (this._financeCommitmentSub) this._financeCommitmentSub.unsubscribe();
  
    switch (value.name) {
      case 'Purchase Additional Asset':
        this.showIncomeGeneratingAsset = true;
        this.formGroup.get('isIncomeGeneratingAsset')?.setValidators([Validators.required]);
        this.formGroup.get('isIncomeGeneratingAsset')?.setValue(false);
        this.formGroup.get('isIncomeGeneratingAsset')?.updateValueAndValidity();

        this._incomeGenSub = this.formGroup.get('isIncomeGeneratingAsset')?.valueChanges.subscribe((isIncome: boolean | null) => {
          this.showExpectedMonthlyIncome = !!isIncome;
          if (isIncome) {
            this.formGroup.get('expectedMonthlyIncome')?.setValidators([Validators.required]);
          } else {
            this.resetField('expectedMonthlyIncome', null);
          }
          this.formGroup.get('expectedMonthlyIncome')?.updateValueAndValidity();
        });
        break;
      case 'Replace Existing Asset':
        this.showExistingFinanceCommitments = true;
        this.formGroup.get('hasExistingFinanceCommitments')?.setValidators([Validators.required]);
        this.formGroup.get('hasExistingFinanceCommitments')?.setValue(false);
        this.formGroup.get('hasExistingFinanceCommitments')?.updateValueAndValidity();

        this._financeCommitmentSub = this.formGroup.get('hasExistingFinanceCommitments')?.valueChanges.subscribe((hasCommitment: boolean | null) => {
          this.showLender = !!hasCommitment;
          this.showLoanValue = !!hasCommitment;
          if (hasCommitment) {
            this.formGroup.get('lender')?.setValidators([Validators.required]);
            // Also show loan value field when there are finance commitments
            this.formGroup.get('loanValue')?.setValidators([Validators.required]);
          } else {
            this.resetField('lender', null);
            this.resetField('loanValue', null);
          }
          this.formGroup.get('lender')?.updateValueAndValidity();
          this.formGroup.get('loanValue')?.updateValueAndValidity();
        });
        break;
      case 'Refinance Existing Borrowing':
        this.showLender = true;
        this.showLoanValue = true;
        this.formGroup.get('lender')?.setValidators([Validators.required]);
        this.formGroup.get('loanValue')?.setValidators([Validators.required]);
        this.formGroup.get('lender')?.updateValueAndValidity();
        this.formGroup.get('loanValue')?.updateValueAndValidity();
        break;
      case 'Refinance ATO Debt':
        this.showTaxBalance = true;
        this.formGroup.get('taxBalance')?.setValidators([Validators.required]);
        this.formGroup.get('taxBalance')?.updateValueAndValidity();
        break;
      case 'Purchase of Another Business':
        this.showPurchasePrice = true;
        this.formGroup.get('purchasePrice')?.setValidators([Validators.required]);
        this.formGroup.get('purchasePrice')?.updateValueAndValidity();
        break;
      case 'Business Expansion/Growth Capital':
        this.showPurposeDescription = true;
        this.furtherDescriptionLabel = 'please explain how funds will be used';
        this.formGroup.get('purposeDescription')?.setValidators([Validators.required]);
        this.formGroup.get('purposeDescription')?.updateValueAndValidity();
        break;
      case 'Managing Cashflow':
        this.showPurposeDescription = true;
        this.furtherDescriptionLabel = 'please explain why a facility is needed';
        this.formGroup.get('purposeDescription')?.setValidators([Validators.required]);
        this.formGroup.get('purposeDescription')?.updateValueAndValidity();
        break;
      default:
        break;
    }
  }

  updateFilteredOptions() {
    let filtered: LoanPurposeOption[] = LoanPurposeValueOptions;

    if (this.applicationType === 'AssetFinance') {
      const type = this.transactionType?.type;
      if (isDealerOrPrivateOrSALB(type)) {
        filtered = LoanPurposeValueOptions.filter(
          (option) =>
            option.name === 'Purchase Additional Asset' ||
            option.name === 'Replace Existing Asset'
        );
      } else if (isRefinanceOrBalloon(type)) {
        filtered = LoanPurposeValueOptions.filter(
          (option) =>
            option.name === 'Refinance Existing Borrowing' ||
            option.name === 'Purchase of Financial Instruments' ||
            option.name === 'Purchase of Residential Property'
        );
      } else if (isCapitalRaise(type)) {
        filtered = LoanPurposeValueOptions.filter(
          (option) =>
            option.name !== 'Funding Insurance Premium' &&
            option.name !== 'Purchase of Residential Property' && option.name !== 'Purchase of Financial Instruments'
        );
      }
    } else if (this.applicationType === 'InsurancePremium') {
      filtered = LoanPurposeValueOptions.filter(
        (option) => option.name === 'Funding Insurance Premium'
      );
    } else if (this.applicationType === 'BusinessOverdraft' && this.hasActiveOverdraftAccount) {
      filtered = [...OverdraftRenewalPurposeOptions];
    } else {
      filtered = LoanPurposeValueOptions.filter(
        option => !this.READ_ONLY_TYPES.includes(option.name) && option.name !== 'Funding Insurance Premium'
      );
    }

    const seen = new Set();
    this.filteredOptions = filtered.filter((opt) => {
      if (seen.has(opt.name)) return false;
      seen.add(opt.name);
      return true;
    });
  }

  isDropdownDisabled(): boolean {
    return this.applicationType === 'AssetFinance' && this.transactionType === null;
  }

  isOptionDisabled(option: LoanPurposeOption): boolean {
    if (
      this.applicationType === 'AssetFinance' &&
      (this.transactionType?.type === 'Refinance' ||
        this.transactionType?.type === 'Balloon Refinance')
    ) {
      return option ? this.READ_ONLY_TYPES.includes(option.name) : false;
    }

    // Check disabled states for overdraft applications with active accounts
    if (this.applicationType === 'BusinessOverdraft' && this.hasActiveOverdraftAccount && option) {
      const purposeName = option.name;
      
      if (purposeName === 'Limit Increase') {
        return this.limitIncreaseDisabled === true;
      }
      if (purposeName === 'Limit Decrease') {
        return this.limitDecreaseDisabled === true;
      }
      if (purposeName === 'Renewal') {
        return this.renewalDisabled === true;
      }
    }

    return false;
  }
}

